/**
 * Flow Optimizer — formula weight validation (frontend save-gate).
 * For Media Devoted | Created By Ciro Autiero
 *
 * Manual weights must each total exactly 100% per layer (paths/landers/offers)
 * and per blocks-group's sub-formulas. The backend score engine re-normalizes
 * any raw weights to proportional shares regardless, so this is purely a UX /
 * data-hygiene gate — it never changes scoring. An untouched (all-zero / unset)
 * set is treated as valid: the editor shows it as an equal split summing to 100.
 */

import { ALL_LAYERS, readGroupWeight } from '@trafficpulse/shared';
import type { FlowLayer, FormulaConfig, FormulaGroup } from '@trafficpulse/shared';

import { groupLayers } from './per-layer-weights.utils';

const LAYER_TITLE: Record<FlowLayer, string> = {
  paths: 'Paths',
  landers: 'Landers',
  offers: 'Offers',
};

export interface WeightTotalIssue {
  /** Human label of the offending set — a layer name or a group name. */
  label: string;
  /** Current sum the user must bring to exactly 100. */
  total: number;
}

function layerIssues(formula: FormulaConfig): WeightTotalIssue[] {
  const issues: WeightTotalIssue[] = [];
  for (const layer of ALL_LAYERS) {
    const ids = formula.groups
      .filter((g) => groupLayers(g).includes(layer))
      .map((g) => g.id);
    if (ids.length < 2) continue; // 0 or 1 group → effectively 100, nothing to share
    const total = ids.reduce(
      (sum, id) => sum + readGroupWeight(formula.groupWeights, id, layer),
      0,
    );
    if (total === 0) continue; // untouched → equal-split display, valid
    if (total !== 100) issues.push({ label: LAYER_TITLE[layer], total });
  }
  return issues;
}

function subformulaIssue(group: FormulaGroup): WeightTotalIssue | null {
  if (group.mode !== 'blocks') return null;
  const subs = group.blocks?.subformulas ?? [];
  if (subs.length < 2) return null;
  if (!subs.some((s) => typeof s.weight === 'number')) return null; // equal split
  const total = subs.reduce(
    (sum, s) => sum + (typeof s.weight === 'number' ? s.weight : 0),
    0,
  );
  if (total === 100) return null;
  return { label: group.name || 'Sub-formula weights', total };
}

/** Returns one issue per layer / blocks-group whose weights do not total 100. */
export function validateFormulaWeights(
  formula: FormulaConfig | undefined | null,
): WeightTotalIssue[] {
  if (!formula?.groups?.length) return [];
  const issues = layerIssues(formula);
  for (const group of formula.groups) {
    const issue = subformulaIssue(group);
    if (issue) issues.push(issue);
  }
  return issues;
}

export function formulaWeightsValid(
  formula: FormulaConfig | undefined | null,
): boolean {
  return validateFormulaWeights(formula).length === 0;
}
